<script setup>
import { computed, onMounted, watch, nextTick } from 'vue';
import Tooltip from '../Atoms/Tooltip.vue';
import IconBase from '../icon/IconBase.vue';
import IconInfo from '../icon/IconInfo.vue';
import { useVodStorageUsage } from '@/composables/useVodStorageUsage';

// Same source as the Billing page, shared with the plan-limit banner so the
// indicator and the banner never disagree about whether the plan is full.
const { usedBytes, maxBytes, maxVodStorage, ratio, isExceeded, storageSubscription, fetchUsage } = useVodStorageUsage();

const afterPaint = () => new Promise((resolve) => {
	if (typeof requestAnimationFrame !== 'function') {
		setTimeout(resolve, 0)
		return
	}
	requestAnimationFrame(() => requestAnimationFrame(resolve))
})

onMounted(async () => {
	await nextTick()
	await afterPaint()
	fetchUsage()
});
watch(storageSubscription, fetchUsage);

const percent = computed(() => {
	if (!maxBytes.value) return 0;
	return Math.min(100, Math.round(ratio.value * 100));
});
// Red once the quota is exceeded, gold when close, brand colour otherwise.
const ringColor = computed(() => {
	if (isExceeded.value) return 'var(--c-error-light, #f1587d)';
	if (ratio.value >= 0.9) return 'var(--c-gold, #f0bb6c)';
	return 'var(--c-main-light, #6366f1)';
});

const formatBytes = (size) => {
	if (!size || size <= 0) return '0 GB';
	const i = Math.floor(Math.log(size) / Math.log(1000));
	const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
	return `${(size / Math.pow(1000, i)).toFixed(1) * 1} ${sizes[i]}`;
};

// donut geometry
const R = 6;
const C = 2 * Math.PI * R;
const dash = computed(() => `${(percent.value / 100) * C} ${C}`);

const tooltip = computed(() =>
	`<div class="text-left">
		<p class="${isExceeded.value ? 'text-error-light' : 'text-surface-9'} font-medium mb-1">${percent.value}% storage used${isExceeded.value ? ' — limit exceeded' : ''}</p>
		<p class="text-surface-8 text-s-xs mb-2">${formatBytes(usedBytes.value)} of ${maxVodStorage.value ? maxVodStorage.value + ' GB' : '—'}</p>
		<p class="text-surface-8 text-s-xs">Need more space? <a class="text-main underline" href="/app/subscribe">Upgrade your plan anytime.</a></p>
	</div>`
);
</script>

<template>
	<Tooltip :content="tooltip" :html="true" :interactive="true" popperClass="max-w-[260px] text-sm">
		<div class="flex items-center gap-2 px-2.5 h-8 rounded-md ring-1 ring-surface-3 bg-surface-2 cursor-default">
			<span class="text-sm text-surface-text-1">Storage</span>
			<svg width="16" height="16" viewBox="0 0 16 16" class="-rotate-90 shrink-0">
				<circle cx="8" cy="8" :r="R" fill="none" stroke="var(--surface-4)" stroke-width="3.5" />
				<circle cx="8" cy="8" :r="R" fill="none" :stroke="ringColor" stroke-width="3.5"
					stroke-linecap="round" :stroke-dasharray="dash" />
			</svg>
			<icon-base class="shrink-0 text-surface-8" fill="none">
				<icon-info />
			</icon-base>
		</div>
	</Tooltip>
</template>
